import { useEffect, useState } from 'react'

// Modal for adding or editing a single supplier.
// When `supplier` is null/undefined, the modal is in "create new" mode.
// Opens from Settings → Suppliers list (tap row to edit, tap + to add).
export default function SupplierEditModal({
  open,
  onClose,
  onSave,        // (id|null, patch) → Promise
  onCreate,      // ({ name, contact, notes }) → Promise<row>
  supplier,      // optional; null = create mode
}) {
  const [form, setForm] = useState({ name: '', contact: '', notes: '', active: true })
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)

  const isCreateMode = !supplier

  useEffect(() => {
    if (open) {
      if (supplier) {
        setForm({
          name: supplier.name ?? '',
          contact: supplier.contact ?? '',
          notes: supplier.notes ?? '',
          active: !!supplier.active,
        })
      } else {
        setForm({ name: '', contact: '', notes: '', active: true })
      }
      setError(null)
    }
  }, [open, supplier])

  if (!open) return null

  function set(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  async function handleSubmit(e) {
    e?.preventDefault?.()
    if (submitting) return
    if (!form.name.trim()) {
      setError('Supplier name is required.')
      return
    }

    setSubmitting(true)
    setError(null)
    try {
      if (isCreateMode) {
        await onCreate({
          name: form.name,
          contact: form.contact,
          notes: form.notes,
        })
      } else {
        await onSave(supplier.id, {
          name: form.name.trim(),
          contact: form.contact.trim() || null,
          notes: form.notes.trim() || null,
          active: form.active,
        })
      }
      onClose?.()
    } catch (e2) {
      // eslint-disable-next-line no-console
      console.error('[SupplierEditModal] save failed', e2)
      setError('Could not save. Check your connection and try again.')
    } finally {
      // Always reset — modal stays mounted between opens, so without
      // finally the `submitting` state would persist after a successful
      // save and disable the submit button on next open.
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-40">
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-black/50 animate-fade-in"
      />

      <form
        onSubmit={handleSubmit}
        className="animate-sheet-up absolute inset-x-0 bottom-0 max-h-[92vh] overflow-y-auto rounded-t-2xl bg-white shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-label={isCreateMode ? 'Add supplier' : `Edit ${supplier.name}`}
      >
        <div className="sticky top-0 z-10 bg-white pt-2">
          <div className="mx-auto h-1.5 w-12 rounded-full bg-black/15" />
          <div className="flex items-center justify-between px-4 py-3">
            <h2 className="text-xl font-medium">
              {isCreateMode ? 'Add supplier' : `Edit · ${supplier.name}`}
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="min-h-btn min-w-[48px] rounded-lg px-3 text-base font-medium text-black/70 hover:bg-black/5"
              aria-label="Cancel"
            >
              Cancel
            </button>
          </div>
          {error && (
            <div role="alert" className="mx-4 mb-2 rounded-lg bg-red-600 px-3 py-2 text-sm font-medium text-white shadow">
              {error}
            </div>
          )}
        </div>

        <div className="space-y-4 px-4 pb-4">
          <div>
            <label className="mb-1 block text-sm font-medium" htmlFor="sup-name">Name</label>
            <input
              id="sup-name"
              type="text"
              value={form.name}
              onChange={(e) => set('name', e.target.value)}
              placeholder="e.g. US Foods"
              className="w-full rounded-lg border border-black/15 px-4 py-3 text-base outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/30"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium" htmlFor="sup-contact">
              Contact (optional)
            </label>
            <input
              id="sup-contact"
              type="text"
              value={form.contact}
              onChange={(e) => set('contact', e.target.value)}
              placeholder="Mike, 555-0142 / local rep / etc."
              className="w-full rounded-lg border border-black/15 px-4 py-3 text-base outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/30"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium" htmlFor="sup-notes">
              Notes (optional)
            </label>
            <textarea
              id="sup-notes"
              rows={3}
              value={form.notes}
              onChange={(e) => set('notes', e.target.value)}
              placeholder="Delivery schedule, payment terms, etc."
              className="w-full rounded-lg border border-black/15 px-4 py-3 text-base outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/30"
            />
          </div>

          {!isCreateMode && (
            <div className="flex items-center justify-between rounded-lg border border-black/10 px-3 py-3">
              <div>
                <div className="text-sm font-medium">Active</div>
                <div className="text-xs text-black/55">
                  Inactive suppliers are hidden from the Receive form and Reorder screen.
                </div>
              </div>
              <input
                type="checkbox"
                checked={form.active}
                onChange={(e) => set('active', e.target.checked)}
                className="h-6 w-6 rounded border-black/30 text-blue-600 focus:ring-blue-600"
              />
            </div>
          )}

          {/* Error moved to sticky header. */}
        </div>

        <div className="sticky bottom-0 z-10 border-t border-black/5 bg-white px-4 pt-3 safe-bottom">
          <button
            type="submit"
            disabled={submitting}
            className="min-h-btn w-full rounded-lg bg-blue-600 px-4 py-4 text-xl font-medium text-white shadow-sm hover:bg-blue-700 active:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {submitting ? 'Saving…' : isCreateMode ? 'Add supplier' : 'Save changes'}
          </button>
        </div>
      </form>
    </div>
  )
}
